import { useEffect, useMemo, useState } from 'react';
import { getArticles, getCosmetics, getNews, getSeason, getShop, type LiveCosmetic, type LiveNewsItem, type LiveShopItem, type PublishedArticle, type SeasonInfo } from './api';
import { demoEvents, demoNewsItems, demoShopItems, cosmeticTypes } from './data/demo';
import { copy, type Locale } from './i18n';
import type { PageKey } from './types';

type LoadState = 'loading' | 'ready' | 'error';
type RouteKey = PageKey | 'adminSystem';

type SystemHealthPayload = {
  ok: boolean;
  checkedAt: string;
  database: { connected: boolean };
  live: Array<{
    name: string;
    ok: boolean;
    status: number | null;
    latencyMs: number;
  }>;
  history: {
    sources: Array<{
      source: string;
      last_attempt_at: string | null;
      last_success_at: string | null;
      last_error_at: string | null;
      consecutive_failures: number;
      last_http_status: number | null;
      last_latency_ms: number | null;
      last_item_count: number;
    }>;
    recentRuns: Array<{
      id: number;
      source: string;
      source_key: string | null;
      started_at: string;
      finished_at: string;
      ok: number;
      http_status: number | null;
      latency_ms: number | null;
      item_count: number;
      trigger: string;
      error_code: string | null;
    }>;
  };
};

type StatusPayload = {
  ok: boolean;
  source?: string;
  fetchedAt?: string;
  fortnite?: {
    status?: string;
    components?: Array<{ id: string; name: string; status: string }>;
  };
  incidents?: Array<{ id: string; name: string; status: string; impact: string }>;
};

const ADMIN_URL = 'https://nexus-admin.eminem13981398.workers.dev/';

const pagePaths: Record<PageKey, string> = {
  home: '/',
  shop: '/shop',
  news: '/news',
  cosmetics: '/cosmetics',
  events: '/events',
  settings: '/settings'
};

const primaryPages: PageKey[] = ['home', 'shop', 'news', 'cosmetics', 'events'];

function pageFromPath(pathname: string): RouteKey {
  const clean = pathname.replace(/\/+$/, '') || '/';
  if (clean === '/admin/system') return 'adminSystem';
  const found = (Object.entries(pagePaths) as [PageKey, string][]).find(([, path]) => path === clean);
  return found?.[0] ?? 'home';
}

function App() {
  const [locale, setLocale] = useState<Locale>(() => {
    const stored = localStorage.getItem('nexus.locale');
    return stored === 'ar' ? 'ar' : 'en';
  });
  const [page, setPage] = useState<RouteKey>(() => pageFromPath(window.location.pathname));
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [reduceMotion, setReduceMotion] = useState(() => localStorage.getItem('nexus.reduceMotion') === '1');
  const [compactMode, setCompactMode] = useState(() => localStorage.getItem('nexus.compactMode') === '1');

  const [status, setStatus] = useState<StatusPayload | null>(null);
  const [statusState, setStatusState] = useState<LoadState>('loading');

  const [shopItems, setShopItems] = useState<LiveShopItem[]>([]);
  const [shopFetchedAt, setShopFetchedAt] = useState<string | null>(null);
  const [shopState, setShopState] = useState<LoadState>('loading');

  const [newsItems, setNewsItems] = useState<LiveNewsItem[]>([]);
  const [newsFetchedAt, setNewsFetchedAt] = useState<string | null>(null);
  const [officialNewsUrl, setOfficialNewsUrl] = useState('https://www.fortnite.com/news');
  const [newsState, setNewsState] = useState<LoadState>('loading');
  const [publishedArticles, setPublishedArticles] = useState<PublishedArticle[]>([]);
  const [articlesState, setArticlesState] = useState<LoadState>('loading');

  const [cosmetics, setCosmetics] = useState<LiveCosmetic[]>([]);
  const [cosmeticsState, setCosmeticsState] = useState<LoadState>('loading');

  const [seasonInfo, setSeasonInfo] = useState<SeasonInfo | null>(null);
  const [seasonSourceUrl, setSeasonSourceUrl] = useState('https://www.fortnite.com/battle-pass');
  const [seasonState, setSeasonState] = useState<LoadState>('loading');
  const [clock, setClock] = useState(() => Date.now());

  const [systemHealth, setSystemHealth] = useState<SystemHealthPayload | null>(null);
  const [systemState, setSystemState] = useState<LoadState>('loading');

  const t = useMemo(() => copy[locale], [locale]);

  useEffect(() => {
    localStorage.setItem('nexus.locale', locale);
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr';
  }, [locale]);

  useEffect(() => {
    localStorage.setItem('nexus.reduceMotion', reduceMotion ? '1' : '0');
    document.documentElement.classList.toggle('reduce-motion', reduceMotion);
  }, [reduceMotion]);

  useEffect(() => {
    localStorage.setItem('nexus.compactMode', compactMode ? '1' : '0');
    document.documentElement.classList.toggle('compact-mode', compactMode);
  }, [compactMode]);

  useEffect(() => {
    const onPopState = () => {
      setPage(pageFromPath(window.location.pathname));
      window.scrollTo({ top: 0, behavior: 'instant' });
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    if (page === 'home') {
      document.title = 'NEXUS | Fortnite Intelligence';
      return;
    }
    if (page === 'adminSystem') {
      document.title = 'System Health | NEXUS Admin';
      return;
    }
    document.title = `${t.nav[page]} | NEXUS`;
  }, [page, t]);

  useEffect(() => {
    let cancelled = false;

    const loadStatus = async () => {
      try {
        const response = await fetch('/api/status', { headers: { accept: 'application/json' } });
        if (!response.ok) throw new Error('status unavailable');
        const payload = await response.json() as StatusPayload;
        if (cancelled) return;
        setStatus(payload);
        setStatusState(payload.ok ? 'ready' : 'error');
      } catch {
        if (cancelled) return;
        setStatus(null);
        setStatusState('error');
      }
    };

    loadStatus();
    const interval = window.setInterval(loadStatus, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setShopState('loading');
    setNewsState('loading');

    getShop(locale, controller.signal)
      .then((data) => {
        setShopItems(data.items);
        setShopFetchedAt(data.fetchedAt);
        setShopState('ready');
      })
      .catch(() => {
        if (!controller.signal.aborted) setShopState('error');
      });

    getNews(locale, controller.signal)
      .then((data) => {
        setNewsItems(data.items);
        setNewsFetchedAt(data.fetchedAt);
        setOfficialNewsUrl(data.officialNewsUrl);
        setNewsState('ready');
      })
      .catch(() => {
        if (!controller.signal.aborted) setNewsState('error');
      });

    setArticlesState('loading');
    getArticles(locale, controller.signal)
      .then((data) => {
        setPublishedArticles(data.items);
        setArticlesState('ready');
      })
      .catch(() => {
        if (!controller.signal.aborted) setArticlesState('error');
      });

    return () => controller.abort();
  }, [locale]);

  useEffect(() => {
    const controller = new AbortController();

    getSeason(controller.signal)
      .then((data) => {
        setSeasonInfo(data.season);
        setSeasonSourceUrl(data.sourceUrl);
        setSeasonState('ready');
      })
      .catch(() => {
        if (!controller.signal.aborted) setSeasonState('error');
      });

    const interval = window.setInterval(() => setClock(Date.now()), 30_000);

    return () => {
      controller.abort();
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setCosmeticsState('loading');
      getCosmetics(
        locale,
        { query: query.trim() || undefined, type: typeFilter, limit: 32 },
        controller.signal
      )
        .then((data) => {
          setCosmetics(data.items);
          setCosmeticsState('ready');
        })
        .catch(() => {
          if (!controller.signal.aborted) setCosmeticsState('error');
        });
    }, query.trim() ? 350 : 0);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [locale, query, typeFilter]);

  const loadSystemHealth = async () => {
    setSystemState('loading');
    try {
      const response = await fetch('/api/admin/system', {
        headers: { accept: 'application/json' },
        cache: 'no-store'
      });
      const payload = await response.json() as SystemHealthPayload;
      setSystemHealth(payload);
      setSystemState('ready');
    } catch {
      setSystemHealth(null);
      setSystemState('error');
    }
  };

  useEffect(() => {
    if (page !== 'adminSystem') return;
    window.location.replace(ADMIN_URL);
  }, [page]);

  const navigate = (next: PageKey) => {
    if (next === page) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    window.history.pushState({}, '', pagePaths[next]);
    setPage(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatTime = (value: string | null | undefined) => {
    if (!value) return t.common.unavailable;
    return new Date(value).toLocaleTimeString(locale === 'ar' ? 'ar-SA' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const typeLabel = (type: string) => {
    if (locale === 'en') return type.toUpperCase();
    const value = type.toLowerCase();
    const ar: Record<string, string> = {
      outfit: 'زي',
      emote: 'رقصة',
      bundle: 'حزمة',
      pickaxe: 'أداة حصاد',
      glider: 'مظلة',
      'harvesting tool': 'أداة حصاد',
      backbling: 'زينة ظهر',
      'back bling': 'زينة ظهر',
      wrap: 'غلاف',
      music: 'موسيقى',
      loading: 'شاشة تحميل'
    };
    return ar[value] ?? type;
  };

  const renderSourceNotice = () => (
    <div className="preview-notice" role="status">
      <span className="preview-pill">{t.common.liveData}</span>
      <p>{t.notice}</p>
    </div>
  );

  const renderDemoItemCard = (item: (typeof demoShopItems)[number], index: number) => (
    <article className="shop-card" key={item.id}>
      <div className={`shop-art art-${(index % 5) + 1}`}>
        <span>{t.common.fallback}</span>
        <div className="item-silhouette" aria-hidden="true" />
      </div>
      <div className="shop-info">
        <div>
          <h3>{item.name}</h3>
          <p>{typeLabel(item.type)}</p>
        </div>
        <strong>{item.price.toLocaleString(locale === 'ar' ? 'ar-SA' : 'en-US')} V</strong>
      </div>
    </article>
  );

  const renderLiveShopCard = (item: LiveShopItem, index: number) => (
    <article className="shop-card" key={item.id}>
      <div className={`shop-art live-shop-art art-${(index % 5) + 1}`}>
        <span>{item.layout}</span>
        {item.image ? <img src={item.image} alt="" loading="lazy" /> : <div className="item-silhouette" aria-hidden="true" />}
      </div>
      <div className="shop-info">
        <div>
          <h3>{item.name}</h3>
          <p>{typeLabel(item.type)}{item.rarity ? ` · ${item.rarity}` : ''}</p>
        </div>
        <strong>{item.price.toLocaleString(locale === 'ar' ? 'ar-SA' : 'en-US')} V</strong>
      </div>
    </article>
  );

  const renderCosmeticCard = (item: LiveCosmetic, index: number) => (
    <article className="shop-card cosmetic-card" key={item.id}>
      <div className={`shop-art live-shop-art art-${(index % 5) + 1}`}>
        <span>{item.rarity ?? t.common.live}</span>
        {item.image ? <img src={item.image} alt="" loading="lazy" /> : <div className="item-silhouette" aria-hidden="true" />}
      </div>
      <div className="shop-info">
        <div>
          <h3>{item.name}</h3>
          <p>{typeLabel(item.type)}</p>
        </div>
      </div>
    </article>
  );

  const homeShop = shopState === 'ready' && shopItems.length > 0 ? shopItems.slice(0, 3) : [];

  const seasonClock = useMemo(() => {
    if (!seasonInfo) return null;

    const start = Date.parse(seasonInfo.endDateStartUtc);
    const end = Date.parse(seasonInfo.endDateEndUtc);

    if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
    if (clock >= start && clock < end) return { state: 'today' as const, days: 0, hours: 0, minutes: 0 };
    if (clock >= end) return { state: 'ended' as const, days: 0, hours: 0, minutes: 0 };

    const diff = Math.max(0, start - clock);
    return {
      state: 'countdown' as const,
      days: Math.floor(diff / 86_400_000),
      hours: Math.floor((diff % 86_400_000) / 3_600_000),
      minutes: Math.floor((diff % 3_600_000) / 60_000)
    };
  }, [seasonInfo, clock]);

  const seasonEndLabel = seasonInfo
    ? new Date(seasonInfo.endDateStartUtc).toLocaleDateString(locale === 'ar' ? 'ar-SA' : 'en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: seasonInfo.timeZone
      })
    : '';

  const SeasonClock = ({ embedded = false }: { embedded?: boolean }) => {
    if (seasonState !== 'ready' || !seasonInfo || !seasonClock) return null;

    return (
      <section className={`season-clock-section ${embedded ? 'season-clock-embedded' : 'section compact-top'}`}>
        <div className="season-clock-copy">
          <p className="eyebrow">{t.home.seasonClock}</p>
          <h2>{seasonInfo.label}</h2>
          <p>
            {seasonClock.state === 'today'
              ? t.home.endsToday
              : seasonClock.state === 'ended'
                ? t.home.ended
                : `${t.home.seasonEnds} ${seasonEndLabel}`}
          </p>
          <a href={seasonSourceUrl} target="_blank" rel="noreferrer">{t.home.officialSeasonSource}</a>
        </div>

        {seasonClock.state === 'countdown' ? (
          <div className="season-countdown" aria-label={t.home.seasonClock}>
            <div><strong>{seasonClock.days}</strong><span>{t.home.days}</span></div>
            <div><strong>{seasonClock.hours}</strong><span>{t.home.hours}</span></div>
            <div><strong>{seasonClock.minutes}</strong><span>{t.home.minutes}</span></div>
          </div>
        ) : (
          <div className="season-countdown season-countdown-message">
            <strong>{seasonClock.state === 'today' ? t.home.endsToday : t.home.ended}</strong>
          </div>
        )}

        <p className="season-clock-note">{t.home.exactTimePending}</p>
      </section>
    );
  };

  const HomePage = () => (
    <>
      <section className="hero section">
        <div className="hero-copy">
          <p className="eyebrow">{t.home.eyebrow}</p>
          <h1>{t.home.title}</h1>
          <p className="hero-body">{t.home.body}</p>
          <div className="hero-actions">
            <button className="button primary" type="button" onClick={() => navigate('shop')}>{t.home.shopButton}</button>
            <button className="button secondary" type="button" onClick={() => navigate('news')}>{t.home.newsButton}</button>
          </div>
        </div>

        <aside className="hero-feed" aria-label={t.home.liveDesk}>
          <div className="hero-feed-head">
            <div>
              <span className="hero-feed-kicker">{t.home.liveDesk}</span>
              <strong>{t.home.now}</strong>
            </div>
            <span className={`hero-live-indicator ${statusState === 'ready' ? 'is-live' : ''}`} />
          </div>

          <button className="hero-story" type="button" onClick={() => navigate('news')}>
            {(publishedArticles[0]?.coverImageUrl || newsItems[0]?.image || homeShop[0]?.image) && (
              <img
                src={publishedArticles[0]?.coverImageUrl ?? newsItems[0]?.image ?? homeShop[0]?.image ?? ''}
                alt=""
                loading="eager"
              />
            )}
            <div className="hero-story-copy">
              <span>{publishedArticles[0] ? t.home.nexusStory : t.home.latestUpdate}</span>
              <h2>
                {publishedArticles[0]?.title ??
                  (newsState === 'ready' && newsItems[0] ? newsItems[0].title : t.home.newsTitle)}
              </h2>
              <p>
                {publishedArticles[0]?.summary ??
                  (newsState === 'ready' && newsItems[0] ? newsItems[0].body : t.news.body)}
              </p>
            </div>
          </button>

          <div className="hero-feed-meta">
            <div>
              <span>{t.home.statusTitle}</span>
              <strong>
                {statusState === 'ready'
                  ? status?.fortnite?.status === 'operational'
                    ? t.home.statusOperational
                    : t.home.statusAttention
                  : t.common.loading}
              </strong>
            </div>
            <div>
              <span>{t.home.shopFreshness}</span>
              <strong>{shopState === 'ready' ? formatTime(shopFetchedAt) : t.common.loading}</strong>
            </div>
            <div>
              <span>{t.home.editorial}</span>
              <strong>{publishedArticles.length}</strong>
            </div>
          </div>
        </aside>
      </section>

      <section className="section notice-section">{renderSourceNotice()}</section>

      <SeasonClock />

      <section className="briefing section compact-top">
        <div className="section-heading">
          <p className="eyebrow">DAILY OVERVIEW</p>
          <h2>{t.home.briefing}</h2>
          <p>{t.home.briefingBody}</p>
        </div>

        <div className="briefing-grid">
          <button className="brief-card featured clickable-card" type="button" onClick={() => navigate('news')}>
            <div className="card-kicker">IN-GAME NEWS</div>
            <h3>{newsState === 'ready' && newsItems[0] ? newsItems[0].title : t.home.newsTitle}</h3>
            <p>{newsState === 'ready' && newsItems[0] ? newsItems[0].body : t.news.body}</p>
            <span className="card-meta">{newsState === 'ready' ? t.common.live : t.common.loading}</span>
          </button>

          <article className="brief-card">
            <div className="status-line">
              <span className={`status-dot ${
                statusState === 'ready'
                  ? status?.fortnite?.status === 'operational' ? 'live' : 'alert'
                  : 'pending'
              }`} />
              <span>{t.home.statusTitle}</span>
            </div>
            <h3>
              {statusState === 'loading'
                ? t.home.statusLoading
                : statusState === 'error'
                  ? t.home.statusUnavailable
                  : status?.fortnite?.status === 'operational'
                    ? t.home.statusOperational
                    : t.home.statusAttention}
            </h3>
            <p>{statusState === 'ready' ? t.home.statusLive : t.home.statusBody}</p>
            {status?.fetchedAt && <span className="card-meta">{formatTime(status.fetchedAt)}</span>}
          </article>

          <article className="brief-card metric-card">
            <span className="metric-value">4</span>
            <span className="metric-label">{t.home.liveSources}</span>
          </article>
        </div>
      </section>

      <section className="section compact-top">
        <div className="section-heading row">
          <div>
            <p className="eyebrow">ITEM SHOP</p>
            <h2>{t.home.shopTitle}</h2>
          </div>
          <button className="text-link" type="button" onClick={() => navigate('shop')}>{t.common.viewAll}</button>
        </div>
        <div className="shop-grid preview-grid">
          {homeShop.length > 0
            ? homeShop.map(renderLiveShopCard)
            : demoShopItems.slice(0, 3).map(renderDemoItemCard)}
        </div>
      </section>
    </>
  );

  const ShopPage = () => (
    <section className="page section">
      <div className="page-hero">
        <p className="eyebrow">{t.shop.eyebrow}</p>
        <h1>{t.shop.title}</h1>
        <p>{t.shop.body}</p>
      </div>

      {renderSourceNotice()}

      <div className="sync-strip">
        <div>
          <span className="sync-label">{t.shop.lastSync}</span>
          <strong>{shopState === 'ready' ? formatTime(shopFetchedAt) : t.common.unavailable}</strong>
        </div>
        <div className="status-line">
          <span className={`status-dot ${shopState === 'ready' ? 'live' : shopState === 'error' ? 'alert' : 'pending'}`} />
          <span>
            {shopState === 'ready' ? t.shop.live : shopState === 'error' ? t.shop.error : t.common.loading}
          </span>
        </div>
      </div>

      {shopState === 'ready' && shopItems.length > 0 ? (
        <div className="shop-grid full-grid">{shopItems.map(renderLiveShopCard)}</div>
      ) : shopState === 'loading' ? (
        <div className="empty-results">{t.common.loading}</div>
      ) : (
        <>
          <div className="source-note"><span>{t.shop.fallback}</span><span className="source-state">{t.common.fallback}</span></div>
          <div className="shop-grid full-grid">{demoShopItems.map(renderDemoItemCard)}</div>
        </>
      )}
    </section>
  );

  const NewsPage = () => (
    <section className="page section">
      <div className="page-hero">
        <p className="eyebrow">{t.news.eyebrow}</p>
        <h1>{t.news.title}</h1>
        <p>{t.news.body}</p>
      </div>

      {renderSourceNotice()}

      <div className="source-note">
        <span>{t.news.feedLabel}</span>
        <a className="source-link" href={officialNewsUrl} target="_blank" rel="noreferrer">{t.news.official}</a>
      </div>

      {articlesState === 'ready' && publishedArticles.length > 0 && (
        <section className="cms-news-section">
          <div className="section-heading row">
            <div>
              <p className="eyebrow">NEXUS EDITORIAL</p>
              <h2>{t.news.originals}</h2>
            </div>
            <span className="source-state">{publishedArticles.length}</span>
          </div>
          <div className="cms-news-grid">
            {publishedArticles.map((article) => (
              <a
                className="cms-story"
                key={article.id}
                href={article.href ?? '#'}
                target={article.kind === 'external' ? '_blank' : undefined}
                rel={article.kind === 'external' ? 'noreferrer' : undefined}
              >
                {article.coverImageUrl && <img src={article.coverImageUrl} alt="" loading="lazy" />}
                <div className="cms-story-copy">
                  <span className="news-category">{article.kind === 'nexus' ? t.news.original : t.news.externalBrief}</span>
                  <h3>{article.title}</h3>
                  <p>{article.summary}</p>
                  <div className="source-line">
                    <strong>{article.authorName}</strong>
                    <span>· {new Date(article.publishedAt).toLocaleDateString(locale === 'ar' ? 'ar-SA' : 'en-US')}</span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </section>
      )}

      <div className="news-list page-news-list">
        {newsState === 'ready' && newsItems.length > 0 ? (
          newsItems.map((story, index) => (
            <article className="news-row live-news-row" key={story.id}>
              <span className="news-index">{String(index + 1).padStart(2, '0')}</span>
              <div className="news-copy">
                <span className="news-category">LIVE BR FEED</span>
                <h3>{story.title}</h3>
                <p>{story.body}</p>
                <div className="source-line">
                  <span>{t.common.source}</span>
                  <strong>Fortnite-API</strong>
                  {newsFetchedAt && <span>· {formatTime(newsFetchedAt)}</span>}
                </div>
              </div>
              {story.image ? <img className="news-thumb" src={story.image} alt="" loading="lazy" /> : <span className="news-state">{t.common.live}</span>}
            </article>
          ))
        ) : newsState === 'loading' ? (
          <div className="empty-results">{t.common.loading}</div>
        ) : (
          demoNewsItems.map((story, index) => (
            <article className="news-row" key={story.id}>
              <span className="news-index">0{index + 1}</span>
              <div className="news-copy">
                <span className="news-category">{story.kind.toUpperCase()}</span>
                <h3>{story.title}</h3>
                <p>{story.summary}</p>
              </div>
              <span className="news-state">{t.common.fallback}</span>
            </article>
          ))
        )}
      </div>
    </section>
  );

  const CosmeticsPage = () => (
    <section className="page section">
      <div className="page-hero">
        <p className="eyebrow">{t.cosmetics.eyebrow}</p>
        <h1>{t.cosmetics.title}</h1>
        <p>{t.cosmetics.body}</p>
      </div>

      {renderSourceNotice()}

      <div className="filter-bar">
        <label className="search-field">
          <span>{t.cosmetics.search}</span>
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t.cosmetics.search} />
        </label>

        <label className="select-field">
          <span>{t.cosmetics.filter}</span>
          <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
            {cosmeticTypes.map((type) => (
              <option value={type} key={type}>{type === 'all' ? t.cosmetics.all : typeLabel(type)}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="results-meta">
        <span>{cosmeticsState === 'ready' ? `${cosmetics.length} ${t.cosmetics.results}` : t.common.loading}</span>
        <span>{cosmeticsState === 'ready' ? t.common.live : cosmeticsState === 'error' ? t.common.unavailable : ''}</span>
      </div>

      {cosmeticsState === 'ready' && cosmetics.length > 0 ? (
        <div className="shop-grid full-grid">{cosmetics.map(renderCosmeticCard)}</div>
      ) : cosmeticsState === 'loading' ? (
        <div className="empty-results">{t.common.loading}</div>
      ) : (
        <div className="empty-results">{cosmeticsState === 'error' ? t.cosmetics.error : t.cosmetics.noResults}</div>
      )}
    </section>
  );

  const EventsPage = () => (
    <section className="page section">
      <div className="page-hero">
        <p className="eyebrow">{t.events.eyebrow}</p>
        <h1>{t.events.title}</h1>
        <p>{t.events.body}</p>
      </div>

      <SeasonClock embedded />

      <div className="preview-notice">
        <span className="preview-pill">{t.common.planned}</span>
        <p>{t.events.pending}</p>
      </div>

      <div className="events-layout">
        <article className="event-panel">
          <span className="event-status">{t.common.planned}</span>
          <h2>{demoEvents[0].title}</h2>
          <p>{demoEvents[0].note}</p>
        </article>

        <article className="source-plan">
          <div className="section-heading">
            <p className="eyebrow">SOURCE ENGINE</p>
            <h2>{t.events.sourcePlan}</h2>
          </div>
          <div className="schedule-list">
            <div><span>{t.events.newsSource}</span><strong>30 min</strong></div>
            <div><span>{t.events.statusSource}</span><strong>15 min</strong></div>
            <div><span>{t.events.competitiveSource}</span><strong>60 min</strong></div>
          </div>
        </article>
      </div>
    </section>
  );


  const SettingsPage = () => {
    const changelog = locale === 'ar'
      ? [
          ['v1.1', 'إرجاع مزايا النسخة القديمة: الإعدادات، الأسئلة الشائعة، السجل، والعدّادات.'],
          ['v1.0', 'إعادة تصميم الواجهة العامة وربطها ببيانات NEXUS الحقيقية.'],
          ['v0.9', 'تنبيهات داخل اللوحة، نسخ احتياطية يومية، واختبارات استرجاع.'],
          ['v0.8', 'جلب أخبار Fortnite الرسمية إلى Review Queue قبل النشر.'],
          ['v0.7', 'CMS كامل للمقالات والمسودات والنشر المجدول.']
        ]
      : [
          ['v1.1', 'Legacy feature revival: settings, FAQ, changelog and countdown surfaces.'],
          ['v1.0', 'Public redesign built around real NEXUS data instead of abstract visuals.'],
          ['v0.9', 'Dashboard alerts, daily backups and restore validation.'],
          ['v0.8', 'Official Fortnite news ingestion into a review-first workflow.'],
          ['v0.7', 'Full CMS for drafts, scheduled publishing and source briefs.']
        ];

    const faq = locale === 'ar'
      ? [
          ['هل العدّ التنازلي دقيق؟', 'NEXUS يعتمد على تاريخ رسمي من Fortnite، وإذا ما توفرت ساعة دقيقة يوضح ذلك بدل ما يخمّن.'],
          ['هل الأخبار رسمية؟', 'الأخبار الرسمية تدخل من Fortnite إلى Review Queue، ومقالات NEXUS الأصلية تنكتب داخل الموقع.'],
          ['ليش بعض الفعاليات ما تظهر؟', 'أي مصدر ما نثق بدقته أو توقيته نخليه Pending بدل ما نعرض بيانات غلط.'],
          ['هل NEXUS تابع لـ Epic Games؟', 'لا. NEXUS مشروع مستقل وغير تابع لـ Epic Games.']
        ]
      : [
          ['Is the countdown accurate?', 'NEXUS uses an official Fortnite date. If Epic does not publish an exact hour, the site says so instead of guessing.'],
          ['Is the news official?', 'Official Fortnite stories enter a review queue, while NEXUS Originals are written and hosted by NEXUS.'],
          ['Why are some events missing?', 'If a source or timezone cannot be verified, NEXUS keeps it pending rather than publishing unreliable data.'],
          ['Is NEXUS affiliated with Epic Games?', 'No. NEXUS is an independent project and is not affiliated with Epic Games.']
        ];

    const resetPreferences = () => {
      setReduceMotion(false);
      setCompactMode(false);
      setLocale('en');
    };

    return (
      <section className="page section settings-page">
        <div className="page-hero">
          <p className="eyebrow">{t.settings.eyebrow}</p>
          <h1>{t.settings.title}</h1>
          <p>{t.settings.body}</p>
        </div>

        <div className="settings-grid">
          <section className="settings-panel">
            <div className="section-heading">
              <p className="eyebrow">{t.settings.preferencesEyebrow}</p>
              <h2>{t.settings.preferences}</h2>
            </div>

            <div className="settings-list">
              <label className="settings-row">
                <span>
                  <strong>{t.settings.language}</strong>
                  <small>{t.settings.languageBody}</small>
                </span>
                <select value={locale} onChange={(event) => setLocale(event.target.value as Locale)}>
                  <option value="en">English</option>
                  <option value="ar">العربية</option>
                </select>
              </label>

              <label className="settings-row">
                <span>
                  <strong>{t.settings.reduceMotion}</strong>
                  <small>{t.settings.reduceMotionBody}</small>
                </span>
                <input type="checkbox" checked={reduceMotion} onChange={(event) => setReduceMotion(event.target.checked)} />
              </label>

              <label className="settings-row">
                <span>
                  <strong>{t.settings.compactMode}</strong>
                  <small>{t.settings.compactModeBody}</small>
                </span>
                <input type="checkbox" checked={compactMode} onChange={(event) => setCompactMode(event.target.checked)} />
              </label>
            </div>

            <button className="button secondary settings-reset" type="button" onClick={resetPreferences}>
              {t.settings.reset}
            </button>
          </section>

          <section className="settings-panel legacy-panel">
            <div className="section-heading">
              <p className="eyebrow">{t.settings.legacyEyebrow}</p>
              <h2>{t.settings.legacyTitle}</h2>
            </div>
            <p>{t.settings.legacyBody}</p>
            <div className="legacy-feature-grid">
              <div><span>01</span><strong>{t.settings.legacyCountdowns}</strong></div>
              <div><span>02</span><strong>{t.settings.legacyFaq}</strong></div>
              <div><span>03</span><strong>{t.settings.legacyChangelog}</strong></div>
              <div><span>04</span><strong>{t.settings.legacyPreferences}</strong></div>
            </div>
          </section>
        </div>

        <section className="settings-section">
          <div className="section-heading">
            <p className="eyebrow">{t.settings.faqEyebrow}</p>
            <h2>{t.settings.faqTitle}</h2>
          </div>
          <div className="faq-list">
            {faq.map(([question, answer]) => (
              <details key={question}>
                <summary>{question}</summary>
                <p>{answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="settings-section">
          <div className="section-heading">
            <p className="eyebrow">{t.settings.changelogEyebrow}</p>
            <h2>{t.settings.changelogTitle}</h2>
          </div>
          <div className="changelog-list">
            {changelog.map(([version, body]) => (
              <article key={version}>
                <strong>{version}</strong>
                <p>{body}</p>
              </article>
            ))}
          </div>
        </section>
      </section>
    );
  };

  const SystemPage = () => {
    const liveByName = new Map(systemHealth?.live.map((entry) => [entry.name, entry]) ?? []);
    const historyByName = new Map(systemHealth?.history.sources.map((entry) => [entry.source, entry]) ?? []);
    const sourceNames = ['status', 'shop', 'news', 'cosmetics'];

    return (
      <section className="page section admin-system-page">
        <div className="page-hero admin-hero">
          <p className="eyebrow">ADMIN / READ ONLY</p>
          <h1>{t.admin.title}</h1>
          <p>{t.admin.body}</p>
        </div>

        <div className="admin-lock-note">
          <span className="preview-pill">{t.admin.readOnly}</span>
          <p>{t.admin.authNote}</p>
          <button className="button secondary admin-refresh" type="button" onClick={loadSystemHealth}>
            {t.admin.refresh}
          </button>
        </div>

        {systemState === 'loading' && !systemHealth ? (
          <div className="empty-results">{t.common.loading}</div>
        ) : systemState === 'error' && !systemHealth ? (
          <div className="empty-results">{t.admin.unavailable}</div>
        ) : (
          <>
            <div className="admin-summary-grid">
              <article className="admin-summary-card">
                <span>{t.admin.database}</span>
                <strong>{systemHealth?.database.connected ? t.admin.connected : t.admin.disconnected}</strong>
              </article>
              <article className="admin-summary-card">
                <span>{t.admin.sources}</span>
                <strong>{systemHealth?.live.filter((entry) => entry.ok).length ?? 0}/4</strong>
              </article>
              <article className="admin-summary-card">
                <span>{t.admin.lastCheck}</span>
                <strong>{systemHealth?.checkedAt ? formatTime(systemHealth.checkedAt) : t.common.unavailable}</strong>
              </article>
            </div>

            <div className="admin-source-grid">
              {sourceNames.map((name) => {
                const live = liveByName.get(name);
                const history = historyByName.get(name);
                const healthy = Boolean(live?.ok);
                return (
                  <article className="admin-source-card" key={name}>
                    <div className="admin-source-head">
                      <div className="status-line">
                        <span className={`status-dot ${healthy ? 'live' : 'alert'}`} />
                        <strong>{name.toUpperCase()}</strong>
                      </div>
                      <span className="source-state">{healthy ? t.admin.healthy : t.admin.degraded}</span>
                    </div>
                    <dl>
                      <div>
                        <dt>{t.admin.latency}</dt>
                        <dd>{live ? `${live.latencyMs} ms` : '—'}</dd>
                      </div>
                      <div>
                        <dt>{t.admin.lastSuccess}</dt>
                        <dd>{history?.last_success_at ? formatTime(history.last_success_at) : '—'}</dd>
                      </div>
                      <div>
                        <dt>{t.admin.failures}</dt>
                        <dd>{history?.consecutive_failures ?? 0}</dd>
                      </div>
                      <div>
                        <dt>{t.admin.items}</dt>
                        <dd>{history?.last_item_count ?? 0}</dd>
                      </div>
                    </dl>
                  </article>
                );
              })}
            </div>

            <div className="admin-runs">
              <div className="section-heading row">
                <div>
                  <p className="eyebrow">SYNC HISTORY</p>
                  <h2>{t.admin.recentRuns}</h2>
                </div>
                <span className="source-state">{systemHealth?.history.recentRuns.length ?? 0}</span>
              </div>

              <div className="admin-runs-table">
                {(systemHealth?.history.recentRuns ?? []).slice(0, 12).map((run) => (
                  <div className="admin-run-row" key={run.id}>
                    <span className={`status-dot ${run.ok ? 'live' : 'alert'}`} />
                    <strong>{run.source}</strong>
                    <span>{formatTime(run.finished_at)}</span>
                    <span>{run.latency_ms ?? 0} ms</span>
                    <span>{run.item_count} {t.admin.itemsShort}</span>
                    <span>{run.ok ? 'OK' : run.error_code ?? 'ERROR'}</span>
                  </div>
                ))}
                {(systemHealth?.history.recentRuns.length ?? 0) === 0 && (
                  <div className="empty-results compact-empty">{t.admin.noRuns}</div>
                )}
              </div>
            </div>
          </>
        )}
      </section>
    );
  };

  const currentPage = {
    home: <HomePage />,
    shop: <ShopPage />,
    news: <NewsPage />,
    cosmetics: <CosmeticsPage />,
    events: <EventsPage />,
    settings: <SettingsPage />,
    adminSystem: <SystemPage />
  }[page];

  return (
    <div className="site-shell">
      <header className="topbar">
        <button className="brand brand-button" type="button" onClick={() => navigate('home')} aria-label="NEXUS home">
          <span className="brand-mark" aria-hidden="true">N</span>
          <span>NEXUS</span>
        </button>

        <nav className="desktop-nav" aria-label="Primary">
          {primaryPages.map((key) => (
            <button type="button" key={key} className={page === key ? 'active' : ''} onClick={() => navigate(key)}>
              {t.nav[key]}
            </button>
          ))}
        </nav>

        <div className="topbar-actions">
          <button
            className={`settings-button ${page === 'settings' ? 'active' : ''}`}
            type="button"
            onClick={() => navigate('settings')}
            aria-label={t.settings.title}
          >
            ⚙
          </button>
          <button
            className="language-button"
            type="button"
            onClick={() => setLocale(locale === 'en' ? 'ar' : 'en')}
            aria-label={locale === 'en' ? 'Switch to Arabic' : 'Switch to English'}
          >
            {locale === 'en' ? 'AR' : 'EN'}
          </button>
        </div>
      </header>

      <main>{currentPage}</main>

      <footer className="footer">
        <button className="brand footer-brand brand-button" type="button" onClick={() => navigate('home')}>
          <span className="brand-mark" aria-hidden="true">N</span>
          <span>NEXUS</span>
        </button>
        <p>{t.footer}</p>
        <span>{page === 'adminSystem' ? 'Admin' : 'NEXUS v1.1'}</span>
      </footer>

      <nav className="mobile-nav" aria-label="Mobile navigation">
        {primaryPages.map((key) => (
          <button type="button" key={key} className={page === key ? 'active' : ''} onClick={() => navigate(key)}>
            {t.nav[key]}
          </button>
        ))}
      </nav>
    </div>
  );
}

export default App;
